import { z } from 'zod';
/**
 * Payloads of the recipe writes: the `recipes` function's routes, and the
 * `create_recipe` / `update_recipe` SQL functions the app calls straight
 * through PostgREST (migration 20261023100000).
 *
 * Free of any relative import: the `recipes` function loads this file, and
 * neither Deno nor the Supabase CLI — which mounts every file a function
 * imports when the stack starts — resolves the `.js` specifiers TypeScript
 * requires. Which is why `dedupeTags` lives here, re-exported by
 * `utils/tags.ts`.
 */
/**
 * The tags of a recipe, in the order they were given, each one only once.
 *
 * Case is ignored when comparing, and the first spelling wins: « Pimenté » and
 * « pimenté » are the same tag to whoever reads the recipe, and keeping both
 * would only offer a choice nobody wants to make. Blanks disappear entirely.
 */
export function dedupeTags(tags) {
    const seen = new Set();
    const kept = [];
    for (const tag of tags ?? []) {
        const trimmed = tag.trim();
        if (!trimmed)
            continue;
        const key = trimmed.toLocaleLowerCase();
        if (seen.has(key))
            continue;
        seen.add(key);
        kept.push(trimmed);
    }
    return kept;
}
const IngredientAlternativePayload = z.object({
    alternative_name: z.string().min(1),
    quantity_value: z.number().positive().nullable().optional(),
    unit: z.string().nullable().optional(),
    alternative_recipe_id: z.string().uuid().nullable().optional(),
    notes: z.string().nullable().optional(),
});
const IngredientPayload = z.object({
    name: z.string().min(1),
    quantity_value: z.number().positive().nullable().optional(),
    unit: z.string().nullable().optional(),
    ingredient_ref_id: z.string().uuid().nullable().optional(),
    sort_order: z.number().int().nonnegative(),
    alternatives: z.array(IngredientAlternativePayload).optional().default([]),
});
const IngredientGroupPayload = z.object({
    title: z.string().nullable().optional(),
    sort_order: z.number().int().nonnegative(),
    ingredients: z.array(IngredientPayload),
});
const StepPayload = z.object({
    instruction: z.string().min(1),
    sort_order: z.number().int().nonnegative(),
    duration_seconds: z.number().int().positive().nullable().optional(),
    alternative_text: z.string().nullable().optional(),
    technique_id: z.string().uuid().nullable().optional(),
    note: z.string().nullable().optional(),
    /**
     * The step's photograph, as the detail endpoint served it — a storage path
     * resolved to its public URL, or the site's own URL for an imported one. Sent
     * back untouched so a save keeps it; the update route maps it to the stored
     * path and its thumbnail.
     */
    image_url: z.string().max(2048).nullable().optional(),
});
const StepSectionPayload = z.object({
    title: z.string().nullable().optional(),
    sort_order: z.number().int().nonnegative(),
    steps: z.array(StepPayload),
});
const SubRecipePayload = z
    .object({
    recipe_id: z.string().uuid().optional(),
    child_recipe_id: z.string().uuid().optional(),
    sort_order: z.number().int().min(0).default(0),
    is_optional: z.boolean().default(false),
    notes: z.string().nullable().optional(),
})
    .transform((val) => ({
    child_recipe_id: val.child_recipe_id ?? val.recipe_id,
    sort_order: val.sort_order,
    is_optional: val.is_optional,
    notes: val.notes ?? null,
}))
    .refine((val) => !!val.child_recipe_id, {
    message: 'Either recipe_id or child_recipe_id is required',
});
/** The longest variant label: « Version enfants sans lactose » fits, a sentence does not. */
export const VARIANT_LABEL_MAX = 40;
/**
 * The diets a variant can say it serves — `DIETS` of `constants/diets.ts`,
 * spelled out because this file imports nothing (see above). A test keeps the
 * two lists equal.
 */
export const VariantDietSchema = z.enum(['vegetarian', 'vegan', 'gluten_free', 'lactose_free']);
/** What sets a variant apart from its base. Exported for the label's edit. */
export const RecipeVariantFieldsSchema = z.object({
    variant_label: z.string().trim().min(1).max(VARIANT_LABEL_MAX),
    variant_diet: VariantDietSchema.nullable().optional(),
});
const RecipeFieldsSchema = z.object({
    title: z.string().min(1).max(255),
    description: z.string().nullable().optional(),
    type: z.enum(['recipe', 'dish']),
    category: z.string(),
    // A recipe's tags are a set: the column is a plain array and an import can
    // perfectly well hand us « Pimenté » twice, so they are normalised on the way
    // in rather than at every place that reads them.
    tags: z.array(z.string()).default([]).transform(dedupeTags),
    seasons: z.array(z.string()).default([]),
    total_time: z.number().int().positive().nullable().optional(),
    waiting_time: z.number().int().positive().nullable().optional(),
    cooking_time: z.number().int().positive().nullable().optional(),
    baking_time: z.number().int().positive().nullable().optional(),
    servings_value: z.number().positive().nullable().optional(),
    servings_unit: z.string().nullable().optional(),
    source_name: z.string().nullable().optional(),
    source_url: z.string().url().nullable().optional(),
    kid_friendly: z.boolean().optional(),
    ingredient_groups: z.array(IngredientGroupPayload).optional().default([]),
    step_sections: z.array(StepSectionPayload).optional().default([]),
    sub_recipes: z.array(SubRecipePayload).optional().default([]),
});
/**
 * A new recipe, or a new variant of one: `variant_of_recipe_id` and its label
 * come together. The database checks that — a plain object here, which the
 * functions' OpenAPI document can describe — and the rest: same family, a
 * base that is not itself a variant. It gives the variant its base's category.
 */
export const CreateRecipeSchema = RecipeFieldsSchema.extend({
    variant_of_recipe_id: z.string().uuid().nullable().optional(),
    variant_label: RecipeVariantFieldsSchema.shape.variant_label.nullable().optional(),
    variant_diet: VariantDietSchema.nullable().optional(),
});
/**
 * A save of the editor. The attachment is not part of it: a version restored
 * must not move a recipe out of its stack — `update_recipe` ignores it too.
 */
export const UpdateRecipeSchema = RecipeFieldsSchema.partial();
//# sourceMappingURL=recipe-requests.js.map